/**
 * Client-side assignee detection aligned with Horizon ``GET /tickets`` normalized ticket shapes.
 */

export function psaTicketAssigneeCandidates(ticket: unknown): string[] {
  const t = ticket as Record<string, unknown> | null | undefined;
  if (!t || typeof t !== "object") return [];
  const ids: string[] = [];

  const at = t["assigned_to"] as Record<string, unknown> | undefined;
  if (at && typeof at === "object") {
    if (at["id"] != null) ids.push(String(at["id"]));
    if (at["name"] != null) ids.push(String(at["name"]));
  }

  const tech = t["technician"];
  if (tech && typeof tech === "object") {
    const tr = tech as Record<string, unknown>;
    if (tr["userId"] != null) ids.push(String(tr["userId"]));
    if (tr["id"] != null) ids.push(String(tr["id"]));
    if (tr["name"] != null) ids.push(String(tr["name"]));
  }
  if (typeof tech === "string" && tech.trim()) {
    ids.push(tech);
  }

  return ids.map((s) => String(s).trim()).filter(Boolean);
}

/** True when ticket appears assigned to ``psaUserId`` (numeric or string match; case-insensitive for strings). */
export function psaTicketMatchesAssignee(ticket: unknown, psaUserId: string): boolean {
  const needle = String(psaUserId ?? "").trim();
  if (!needle) return false;
  const needleNum = Number(needle);
  const needleNumeric = Number.isFinite(needleNum) && String(needleNum).trim() === needle;

  return psaTicketAssigneeCandidates(ticket).some((c) => {
    if (!c) return false;
    if (needleNumeric) {
      const cn = Number(c);
      if (Number.isFinite(cn) && String(cn).trim() === c && cn === needleNum) return true;
    }
    return c.toLowerCase() === needle.toLowerCase();
  });
}

/** True when no meaningful assignee was resolved from PSA fields (or generic placeholders only). */
export function psaTicketIsUnassigned(ticket: unknown): boolean {
  const raw = psaTicketAssigneeCandidates(ticket);
  if (raw.length === 0) return true;
  if (
    raw.length === 1 &&
    /^(unassigned|nobody|no\s+owner|not\s+assigned|[—\-]|n\/a)$/i.test(String(raw[0]))
  ) {
    return true;
  }
  return false;
}
